import React, { lazy, Suspense } from "react";
import styled from "@emotion/styled";
import { RouteID, routePaths } from "../../routing/routes";
import { Text, Button, CollapsibleSection, Spinner } from "@atlas-int/ui";

import NavBar from "./NavBar";
import { TitleContainer } from "./RankingsOuterGrid";
import { spacing, lineHeight, breakpoints, text } from "@atlas-int/tokens";
import lexicon from "./lexicon";
import { CPProductClass } from "../../types/countryProfileEnums";
import rankingDiagramPng from "./modals/ranking-diagram.png";

const axisMargin = { left: 190, right: 90 };

const RankingsGrid = styled.div`
  padding-bottom: 10vh;
`;

const Wrapper = styled.div`
  max-width: 1020px;
  margin: auto;
`;

const DiagramWrapper = styled.img`
  width: 50%;
  height: auto;
`;

const IntroSection = styled.div`
  margin-right: ${axisMargin.right}px;

  @media (max-width: ${breakpoints.md}) {
    margin-right: 0;
  }
`;

const IntroText = styled.div`
  line-height: ${lineHeight.relaxed};
  color: ${text.primary};

  a {
    color: ${text.link};
    border-bottom-width: 1px;
    border-bottom-style: solid;
    border-bottom-color: transparent;

    &:hover {
      border-bottom-color: ${text.link};
    }
  }
`;

const SectionSpacing = styled.div`
  margin: ${spacing[8]} 0;
`;

const LoadingContainer = styled.div`
  height: 80vh;
  display: flex;
  justify-content: center;
  align-items: center;
`;
const Loading = () => (
  <LoadingContainer>
    <Spinner size="lg" />
  </LoadingContainer>
);

const CountryRankings = lazy(() => import("./countryRankings/index"));
const ProductRankings = lazy(() => import("./productRankings/index"));
const GrowthProjections = lazy(() => import("./growthProjections/index"));

interface Props {
  routeID:
    | RouteID.CountryRankings
    | RouteID.ProductRankings
    | RouteID.GrowthProjections;
  pathParams: Record<string, string | undefined>;
  queryParams: Record<string, string | undefined>;
}

const productClassQueryKey = "productClass";

const normalizeProductClassParam = (rawValue: string | undefined) => {
  if (!rawValue) return undefined;
  const normalized = rawValue.toUpperCase();
  // Support all product classifications
  if (
    normalized === CPProductClass.HS ||
    normalized === CPProductClass.HS92 ||
    normalized === CPProductClass.HS12 ||
    normalized === CPProductClass.HS22 ||
    normalized === CPProductClass.SITC
  ) {
    return normalized;
  }
  return undefined;
};

const Rankings = (props: Props) => {
  const { routeID, pathParams, queryParams } = props;
  const productClassParam = normalizeProductClassParam(
    queryParams[productClassQueryKey],
  );

  let introText: React.ReactNode;
  let content: React.ReactElement<any>;
  if (routeID === RouteID.CountryRankings) {
    introText = (
      <>
        <p
          dangerouslySetInnerHTML={{
            __html: lexicon.introText.country.description,
          }}
        />

        <p
          dangerouslySetInnerHTML={{
            __html: lexicon.introText.country.methodology,
          }}
        />
        <SectionSpacing>
          <CollapsibleSection label={lexicon.modalText.includedCountries}>
            <p>
              The following is a schematic of the procedure used to determine
              the countries that are included in the Atlas Complexity Ranking.
            </p>
            <DiagramWrapper src={rankingDiagramPng} alt="Ranking Diagram" />
          </CollapsibleSection>
        </SectionSpacing>
        <SectionSpacing>
          <CollapsibleSection label={lexicon.modalText.methodology}>
            {lexicon.methodologyModal.text}
          </CollapsibleSection>
        </SectionSpacing>
        <p
          dangerouslySetInnerHTML={{
            __html: lexicon.introText.country.moreInfo,
          }}
        />
      </>
    );
    content = <CountryRankings />;
  } else if (routeID === RouteID.ProductRankings) {
    introText = (
      <>
        <p
          dangerouslySetInnerHTML={{
            __html: lexicon.introText.product,
          }}
        />
        <SectionSpacing>
          <CollapsibleSection label={lexicon.modalText.includedCountries}>
            <p>
              The following is a schematic of the procedure used to determine
              the countries that are included in the Atlas Complexity Ranking.
            </p>
            <DiagramWrapper src={rankingDiagramPng} alt="Ranking Diagram" />
          </CollapsibleSection>
        </SectionSpacing>
        <SectionSpacing>
          <CollapsibleSection label={lexicon.modalText.methodology}>
            {lexicon.methodologyModal.text}
          </CollapsibleSection>
        </SectionSpacing>
      </>
    );
    content = <ProductRankings />;
  } else if (routeID === RouteID.GrowthProjections) {
    introText = (
      <>
        <p
          dangerouslySetInnerHTML={{
            __html: lexicon.introText.growthProjections,
          }}
        />
        <SectionSpacing>
          <CollapsibleSection label={lexicon.modalText.includedCountries}>
            <p>
              The following is a schematic of the procedure used to determine
              the countries that are included in the Atlas Complexity Ranking.
            </p>
            <DiagramWrapper src={rankingDiagramPng} alt="Ranking Diagram" />
          </CollapsibleSection>
        </SectionSpacing>
        <SectionSpacing>
          <CollapsibleSection label={lexicon.modalText.methodology}>
            {lexicon.methodologyModal.text}
          </CollapsibleSection>
        </SectionSpacing>
      </>
    );
    content = (
      <GrowthProjections pathParams={pathParams} queryParams={queryParams} />
    );
  } else {
    introText = null;
    content = <div />;
    ((_: never) => {})(routeID);
  }

  return (
    <RankingsGrid>
      <Wrapper>
        <TitleContainer>
          <Text variant="heading-1">
            Complexity Rankings and Growth Projections
          </Text>
        </TitleContainer>
        <IntroSection>
          <Button
            variant="outline"
            size="lg"
            onClick={() => {
              window.open(
                routePaths[RouteID.GrowthProjectionsPressRelease] ??
                  "/press-release",
                "_blank",
                "noopener,noreferrer",
              );
            }}
          >
            Growth Projections Press Release
          </Button>
          <IntroText>{introText}</IntroText>
        </IntroSection>
        <NavBar productClass={productClassParam} />
        <Suspense fallback={<Loading />}>{content}</Suspense>
      </Wrapper>
    </RankingsGrid>
  );
};

export default Rankings;
